import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, Target, TrendingUp, Shield } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function ATSScoreCheckerPage() {
  return (
    <>
      <Helmet>
        <title>ATS Score Checker - Know Your Score Before You Apply | LoopBaan.ai</title>
        <meta name="description" content="Get your ATS compatibility score in seconds. See exactly how applicant tracking systems see your resume and fix issues before applying." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">FREE TOOL</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Know Your ATS Score{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Before You Apply
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                75% of resumes never reach human eyes. Get your ATS compatibility score and fix issues before they cost you the interview.
              </p>
              <div className="flex justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Check Your Score Free <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: Target,
                  title: "Instant Scoring",
                  description: "Upload your resume and get a comprehensive ATS compatibility score in seconds, not days."
                },
                {
                  icon: TrendingUp,
                  title: "Detailed Analysis",
                  description: "See exactly what ATS systems flag: formatting issues, missing keywords, parsing problems, and more."
                },
                {
                  icon: Shield,
                  title: "Actionable Fixes",
                  description: "Get specific recommendations to improve your score and beat the bots before submitting."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">What You'll Discover</h2>
              <div className="space-y-8">
                {[
                  {
                    step: "1",
                    title: "Overall ATS Compatibility Score",
                    description: "A clear 0-100 score showing how well your resume will pass through applicant tracking systems."
                  },
                  {
                    step: "2",
                    title: "Critical Issues & Warnings",
                    description: "Specific problems that will cause ATS rejection: tables, graphics, unusual formatting, columns, etc."
                  },
                  {
                    step: "3",
                    title: "Keyword Analysis",
                    description: "Compare your resume against job descriptions to see which keywords you're missing."
                  },
                  {
                    step: "4",
                    title: "Formatting Recommendations",
                    description: "Step-by-step guidance to fix every issue and maximize your chances of getting past the bots."
                  }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-xl">
                        {item.step}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-lg">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">Why ATS Optimization Matters</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "75% of resumes are rejected by ATS before a human sees them",
                  "90% of large companies use ATS software",
                  "Simple formatting mistakes can tank your score",
                  "Keywords matter more than you think",
                  "ATS can't read graphics, tables, or columns",
                  "One resume doesn't fit all job descriptions",
                  "Higher scores mean more interview callbacks",
                  "Free check takes 60 seconds or less"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Stop Applying Blind
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Know your ATS score before you click submit. It's free, instant, and could be the difference between getting the interview or getting rejected.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Check Your Score Now <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
